import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Chip,
  Unstable_Grid2 as Grid,
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Skeleton,
  CircularProgress,
} from "@mui/material";

import BusinessIcon from "@mui/icons-material/Business";
import ChurchIcon from "@mui/icons-material/Church";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";

import { useAuth } from "../../../contexts/AuthContext";
import { fetchData } from "../../../fetchData";

import RRDeliveredTasks from "./drawers/rrDelivered";
import RRTaskLog from "./drawers/rrTaskLog";
import RRTaskNotify from "./drawers/rrTaskNotify";
import { handleScoreColor } from "../../../utils";
import NpsProgressBox from "../../../components/NpsProgressBox";
import A1Expiricy from "./drawers/A1Expiricy";
import { useCertificateStatus } from "../../../hooks/useCertificateStatus";
import { Organization, NumCardProps } from "../../../types/certificate";

interface A1Data {
  total: number;
  data: any[];
}

const getTotalByProfile = (organizations: Organization[], profile: string) => {
  return organizations.filter((org) => org.profile?.includes(profile)).length;
};

const getPercentualByProfile = (
  organizations: Organization[],
  profile: string,
) => {
  if (organizations.length === 0) return 0;
  const total = getTotalByProfile(organizations, profile);
  return Math.floor((total / organizations.length) * 100);
};

const NumCard = ({
  title,
  number,
  percent,
  a1,
  action = null,
  loading,
  certificateCount = 0,
  noCertificateCount = 0,
  showCertificateButton = false,
}: NumCardProps) => {
  return (
    <Card sx={{ flex: 1, height: "100%" }}>
      <CardContent sx={{ height: "100%", p: 2 }}>
        <Stack direction="row" spacing={1} sx={{ height: "100%" }}>
          <Box
            sx={{
              width: 4,
              height: "100%",
              borderRadius: 2,
              backgroundColor: "primary.main",
              alignSelf: "stretch",
            }}
          />
          <Stack flex={1} justifyContent="space-between" spacing={1}>
            {loading && <CircularProgress size={32} />}
            {!loading && (
              <div>
                <Typography
                  variant="caption"
                  component="div"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  {title}
                </Typography>
                <Typography variant="h4" component="div" sx={{ mb: 1 }}>
                  {number}
                </Typography>
                {showCertificateButton && a1 ? (
                  <A1Expiricy
                    data={a1.data}
                    certificateCount={certificateCount}
                    noCertificateCount={noCertificateCount}
                    profileFilter={a1.profileFilter}
                    autoFilterProfile={a1.profileFilter}
                  />
                ) : (
                  a1 &&
                  a1.data &&
                  a1.data.length > 0 && <A1Expiricy data={a1.data} />
                )}
              </div>
            )}
            {action && action}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const {
    getInvalidCertificateCountByProfile,
    getNoCertificateCountByProfile,
  } = useCertificateStatus();
  const [report, setReport] = useState<any>({
    loading: true,
    data: [],
    id: "",
    created_at: "",
  });
  const [logID, setLogID] = useState<number | string>("");
  const [loading, setLoading] = useState(true);
  const [allCertificates, setAllCertificates] = useState<Organization[]>([]);
  const [photo, setPhoto] = useState({
    state: false,
    url: "",
  });

  const initPhoto = async () => {
    const response = await fetch(
      `https://pixabay.com/api/?key=${process.env.REACT_APP_PIXABAY_API_KEY}&q=business&image_type=photo&pretty=true&per_page=100&orientation=horizontal`,
    );

    if (response.ok) {
      response.json().then((result) => {
        setPhoto({
          ...photo,
          url: result.hits[Math.floor(Math.random() * 100)].largeImageURL,
        });
      });
    }
  };

  const initOrganizations = async () => {
    const result = await fetchData(`/organizations?limit=1000&status=true`, {
      method: "GET",
    });

    if (result) {
      const allCerts: any[] = [];

      result.rows.map((organization: any) => {
        allCerts.push(organization);
      });

      setAllCertificates(allCerts);
      setLoading(false);
    }
  };

  const initNps = async () => {
    const lastOne = await fetchData(`/surveys/nps?limit=1&order=desc`, {
      method: "GET",
    });

    if (lastOne) {
      try {
        const response = await fetchData(`/surveys/nps/report`, {
          method: "POST",
          body: JSON.stringify({
            npsId: lastOne.rows[0]._id,
            filter: "alltime",
          }),
        });

        if (response) {
          setReport({
            loading: false,
            data: response,
            id: lastOne.rows[0]._id,
            created_at: lastOne.rows[0].created_at,
          });
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    initPhoto();
    initOrganizations();
    initNps();
  }, []);

  const onLoad = () => {
    setPhoto({ ...photo, state: true });
  };

  return (
    <Stack
      direction="column"
      alignItems="stretch"
      spacing={2}
      sx={{ height: "calc(100% - 72px)" }}
    >
      <Box sx={{ height: "350px" }}>
        <Card sx={{ height: "100%" }}>
          <CardContent
            sx={{
              p: 0,
              paddingBottom: "0 !important",
              height: "100%",
              position: "relative",
            }}
          >
            {photo.state && (
              <Stack
                sx={{
                  p: 3,
                  width: "100%",
                  height: "100%",
                  position: "absolute",
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0) 42%, rgba(0,0,0,0.8) 100%)",
                }}
                direction="column"
                justifyContent="flex-end"
                spacing={2}
              >
                <Typography variant="h3" color="#FFF" fontWeight={300}>
                  Olá, {auth?.user.nickname}.
                </Typography>
              </Stack>
            )}
            <img
              src={photo.url}
              onLoad={onLoad}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: !photo.state ? "none" : "flex",
              }}
            />
            {!photo.state && (
              <Skeleton variant="rectangular" width="100%" height="100%" />
            )}
          </CardContent>
        </Card>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Grid container spacing={2} sx={{ height: "100%" }}>
          <Grid xs={12} lg={4} sx={{ height: "100%" }}>
            <Card sx={{ height: "100%" }}>
              {report.loading && (
                <Skeleton variant="rectangular" width="100%" height="100%" />
              )}
              {!report.loading && (
                <CardContent sx={{ height: "100%" }}>
                  <Stack
                    direction="column"
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ height: "100%" }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ width: "100%" }}
                    >
                      <Chip
                        label="Score NPS"
                        variant="outlined"
                        onClick={() => {
                          navigate(`/surveys/nps/${report.id}`);
                        }}
                      />
                      <Typography variant="body2">
                        {new Date(report.created_at).getFullYear()}
                      </Typography>
                    </Stack>
                    <Stack
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        width: 150,
                        height: 150,
                        borderRadius: 150,
                        borderWidth: 7,
                        borderStyle: "solid",
                        borderColor: handleScoreColor(
                          report.data.graph?.score || 0,
                        ),
                      }}
                    >
                      <Typography
                        variant="h2"
                        fontWeight={500}
                        color={handleScoreColor(report.data.graph?.score || 0)}
                      >
                        {report.data.graph?.score || 0}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      sx={{ width: "100%" }}
                      justifyContent="center"
                    >
                      <NpsProgressBox
                        score={report.data.graph?.detractors.percentual || 0}
                        color="#fd6643"
                        radius="5px 0 0 5px"
                      />
                      <NpsProgressBox
                        score={report.data.graph?.passives.percentual || 0}
                        color="#FEC230"
                      />
                      <NpsProgressBox
                        score={report.data.graph?.promoters.percentual || 0}
                        color="#6EAA2E"
                        radius="0 5px 5px 0"
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              )}
            </Card>
          </Grid>
          <Grid xs={12} lg={8}>
            <Stack direction="column" spacing={2} sx={{ height: "100%" }}>
              <Card>
                <CardContent sx={{ pb: "16px !important" }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Chip
                      label="Tarefas"
                      variant="outlined"
                      color="secondary"
                    />
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={2}>
                        <RRTaskNotify />
                        <RRTaskLog taskLog={{ set: setLogID, get: logID }} />
                        <RRDeliveredTasks
                          taskLog={{ set: setLogID, get: logID }}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
                <NumCard
                  title="Clientes"
                  loading={loading}
                  number={allCertificates.length}
                  percent={allCertificates.length > 0 ? 100 : 0}
                  a1={{ total: 0, data: allCertificates }}
                  action={
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        navigate("/organizations", {
                          state: { filter: { status: "true" } },
                        });
                      }}
                    >
                      Ver todos
                    </Button>
                  }
                />
                <NumCard
                  title="Empresas"
                  loading={loading}
                  number={getTotalByProfile(allCertificates, "Empresas")}
                  percent={
                    allCertificates.length > 0
                      ? getPercentualByProfile(allCertificates, "Empresas")
                      : 0
                  }
                  a1={{
                    total: allCertificates.length,
                    data: allCertificates,
                    profileFilter: "Empresas",
                  }}
                  showCertificateButton={true}
                  certificateCount={getInvalidCertificateCountByProfile(
                    allCertificates,
                    "Empresas",
                  )}
                  noCertificateCount={getNoCertificateCountByProfile(
                    allCertificates,
                    "Empresas",
                  )}
                  action={
                    <Button
                      size="small"
                      fullWidth
                      startIcon={<BusinessIcon color="primary" />}
                      variant="outlined"
                      onClick={() => {
                        navigate("/organizations", {
                          state: {
                            filter: { profile: "Empresas", status: "true" },
                          },
                        });
                      }}
                    >
                      Listar
                    </Button>
                  }
                />
                <NumCard
                  title="Igrejas"
                  loading={loading}
                  number={getTotalByProfile(allCertificates, "Igrejas")}
                  percent={
                    allCertificates.length > 0
                      ? getPercentualByProfile(allCertificates, "Igrejas")
                      : 0
                  }
                  a1={{
                    total: allCertificates.length,
                    data: allCertificates,
                    profileFilter: "Igrejas",
                  }}
                  showCertificateButton={true}
                  certificateCount={getInvalidCertificateCountByProfile(
                    allCertificates,
                    "Igrejas",
                  )}
                  noCertificateCount={getNoCertificateCountByProfile(
                    allCertificates,
                    "Igrejas",
                  )}
                  action={
                    <Button
                      size="small"
                      fullWidth
                      startIcon={<ChurchIcon color="primary" />}
                      variant="outlined"
                      onClick={() => {
                        navigate("/organizations", {
                          state: {
                            filter: { profile: "Igrejas", status: "true" },
                          },
                        });
                      }}
                    >
                      Listar
                    </Button>
                  }
                />
                <NumCard
                  title="Terceiro Setor"
                  loading={loading}
                  number={getTotalByProfile(allCertificates, "Terceiro Setor")}
                  percent={
                    allCertificates.length > 0
                      ? getPercentualByProfile(
                          allCertificates,
                          "Terceiro Setor",
                        )
                      : 0
                  }
                  a1={{
                    total: allCertificates.length,
                    data: allCertificates,
                    profileFilter: "Terceiro Setor",
                  }}
                  showCertificateButton={true}
                  certificateCount={getInvalidCertificateCountByProfile(
                    allCertificates,
                    "Terceiro Setor",
                  )}
                  noCertificateCount={getNoCertificateCountByProfile(
                    allCertificates,
                    "Terceiro Setor",
                  )}
                  action={
                    <Button
                      size="small"
                      fullWidth
                      startIcon={<AccountBalanceIcon color="primary" />}
                      variant="outlined"
                      onClick={() => {
                        navigate("/organizations", {
                          state: {
                            filter: {
                              profile: "Terceiro Setor",
                              status: "true",
                            },
                          },
                        });
                      }}
                    >
                      Listar
                    </Button>
                  }
                />
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};

export default Dashboard;
